import React, { useCallback, useEffect, useState } from 'react';

import { MobileOutlined, QrcodeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Tooltip, Typography } from 'antd';

import { useEventDetail } from 'components/Pages/Admin/TabsMyEvents/context/EventDetail';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { startOfDay } from 'date-fns/startOfDay';
import { subDays } from 'date-fns/subDays';
import { YellowTicket } from 'helpers/customIcons';

import PageTitle from '../components/PageTitle';
import AddStaffModal from './components/AddStaffModal';
import { CheckinCard } from './components/Card';
import StaffList from './components/StaffList';
import staffService from './service';

const CheckinStaff = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const eventDetail = useEventDetail();
  const [isLoadingStaffList, setIsLoadingStaffList] = useState(false);
  const eventData = eventDetail?.data.event;
  const eventId = eventData?.id;

  const fetchStaffList = useCallback(() => {
    if (!eventId) return;
    setIsLoadingStaffList(true);
    const subscription = staffService.staffListByEventId(eventId).subscribe({
      next: res => {
        setStaffList(res.event_staff);
        setIsLoadingStaffList(false);
      },
      error: error => {
        console.error('Error fetching staff list:', error);
        setIsLoadingStaffList(false);
      }
    });
    return subscription;
  }, [eventId]);

  useEffect(() => {
    let subscription = fetchStaffList();

    if (!eventData) {
      return () => {
        subscription?.unsubscribe();
      };
    }

    const checkinInitialDate = startOfDay(subDays(eventData.start_date_default, 1));

    const isInCheckinPeriod = isWithinInterval(new Date(), {
      start: checkinInitialDate,
      end: eventData.end_date_default
    });

    if (!isInCheckinPeriod) {
      return () => {
        subscription?.unsubscribe();
      };
    }

    const interval = setInterval(() => {
      subscription = fetchStaffList();
    }, 30000);

    return () => {
      clearInterval(interval);
      subscription?.unsubscribe();
    };
  }, [eventData, eventId, fetchStaffList]);

  const owner = staffList.find(staff => staff.is_owner);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <PageTitle
          title='Equipe de Check-in'
          subtitle='Gerencie sua equipe de credenciamento, adicionando ou removendo os profissionais que farão o check-in dos participantes via QRCode no dia do evento.'
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', margin: '45px 0 0 0' }}>
        <Typography.Title style={{ fontSize: '16px' }}>Credenciadores cadastrados</Typography.Title>
        <div style={{ display: 'flex', gap: '16px' }}>
          <a href={owner?.url} target='_blank' rel='noreferrer'>
            <Button size='large' icon={<QrcodeOutlined />}>
              Acessar Check-in
            </Button>
          </a>
          <Tooltip title={eventData?.is_past_event ? 'Evento encerrado' : ''}>
            <Button
              size='large'
              type='primary'
              icon={<MobileOutlined />}
              onClick={() => setIsModalOpen(true)}
              disabled={eventData?.is_past_event}
            >
              Adicionar Credenciador
            </Button>
          </Tooltip>
        </div>
      </div>
      <AddStaffModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        eventId={eventId}
        setIsModalOpen={setIsModalOpen}
        onSuccess={fetchStaffList}
      />
      <div style={{ marginTop: '24px' }}>
        <StaffList
          data={staffList}
          eventData={eventData}
          setIsModalOpen={setIsModalOpen}
          onRefresh={fetchStaffList}
          loadingStaffList={isLoadingStaffList}
        />
      </div>
      <Row gutter={[16, 16]} align={'stretch'} style={{ marginTop: '48px' }}>
        <Col xs={24} sm={24} md={8}>
          <CheckinCard
            icon={<QrcodeOutlined style={{ fontSize: 28, color: '#FFBC00' }} />}
            title='Como funciona o check-in via QR Code nos eventos da Eduzz'
            text='Descubra como o check-in da Blinket torna a entrada no seu evento mais rápida e segura.'
            buttonText='Veja como funciona o check-in'
            link={'mockLink'}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <CheckinCard
            icon={<YellowTicket />}
            title='Passo a passo para usar o leitor de QR Code no dia do evento'
            text='Prepare sua equipe para um check-in ágil e eficiente, evitando filas e garantindo o controle.'
            buttonText='Acompanhe o passo a passo completo'
            link={'mockLink'}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <CheckinCard
            icon={<QuestionCircleOutlined style={{ fontSize: 28, color: '#FFBC00' }} />}
            title='Dúvidas comuns sobre check-in com QR Code: tudo o que você precisa saber'
            text='Reunimos as perguntas mais frequentes sobre o uso de QR Code para check-in.'
            buttonText='Confira as perguntas mais frequentes'
            link={'mockLink'}
          />
        </Col>
      </Row>
    </>
  );
};

export default CheckinStaff;
